import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "mobileMenu",
    "mobileMenuPanel",
    "mobileMenuClose",
    "mobileToggle",
    "accountMenu",
    "accountTrigger",
    "accountContainer"
  ]

  connect() {
    this.bodyScrollLockActive = false
    this.originalBodyPaddingRight = ""
    this.syncExpandedState()
    this.closeMobileMenuOnDesktop()
  }

  disconnect() {
    this.unlockBodyScroll()
  }

  toggleMobileMenu(event) {
    event.preventDefault()
    if (!this.hasMobileMenuTarget) return

    if (this.mobileMenuTarget.classList.contains("hidden")) {
      this.openMobileMenu()
      return
    }

    this.closeMobileMenu()
  }

  openMobileMenu() {
    this.mobileMenuTarget.classList.remove("hidden")
    this.lockBodyScroll()
    this.closeAccountMenuWithoutFocus()
    this.syncExpandedState()
    this.focusMobileMenu()
  }

  closeMobileMenu(options = {}) {
    if (!this.hasMobileMenuTarget || this.mobileMenuTarget.classList.contains("hidden")) return

    this.mobileMenuTarget.classList.add("hidden")
    this.unlockBodyScroll()
    this.syncExpandedState()

    if (!options.preserveFocus && this.hasMobileToggleTarget) {
      this.mobileToggleTarget.focus()
    }
  }

  closeMobileMenuOnLinkClick(event) {
    if (!event.target.closest("a")) return

    this.closeMobileMenu({ preserveFocus: true })
  }

  trapMobileMenuFocus(event) {
    if (!this.mobileMenuOpen() || event.key !== "Tab" || !this.hasMobileMenuPanelTarget) return

    const focusable = this.mobileMenuFocusableElements()
    if (focusable.length === 0) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    if (!this.mobileMenuPanelTarget.contains(document.activeElement)) {
      event.preventDefault()
      first.focus()
      return
    }

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
      return
    }

    if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  closeMobileMenuOnDesktop() {
    if (!this.hasMobileMenuTarget) return
    if (!window.matchMedia("(min-width: 1024px)").matches) return

    this.mobileMenuTarget.classList.add("hidden")
    this.unlockBodyScroll()
    this.syncExpandedState()
  }

  lockBodyScroll() {
    if (this.bodyScrollLockActive) return

    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth

    this.originalBodyPaddingRight = document.body.style.paddingRight
    document.body.classList.add("ec-mobile-menu-open")
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`
    }

    this.bodyScrollLockActive = true
  }

  unlockBodyScroll() {
    if (!this.bodyScrollLockActive) {
      document.body.classList.remove("ec-mobile-menu-open")
      return
    }

    document.body.classList.remove("ec-mobile-menu-open")
    document.body.style.paddingRight = this.originalBodyPaddingRight || ""
    this.bodyScrollLockActive = false
  }

  toggleAccountMenu(event) {
    event.preventDefault()
    if (!this.hasAccountMenuTarget) return

    const shouldOpen = this.accountMenuTarget.classList.contains("hidden")
    if (shouldOpen) this.closeMobileMenu({ preserveFocus: true })

    this.accountMenuTarget.classList.toggle("hidden", !shouldOpen)
    this.syncExpandedState()
  }

  closeAccountMenu() {
    if (!this.closeAccountMenuWithoutFocus()) return
    if (this.hasAccountTriggerTarget) this.accountTriggerTarget.focus()
  }

  closeAccountMenuWithoutFocus() {
    if (!this.hasAccountMenuTarget || this.accountMenuTarget.classList.contains("hidden")) return false

    this.accountMenuTarget.classList.add("hidden")
    this.syncExpandedState()
    return true
  }

  closeAccountMenuOnOutsideClick(event) {
    if (!this.hasAccountMenuTarget || this.accountMenuTarget.classList.contains("hidden")) return
    if (this.hasAccountContainerTarget && this.accountContainerTarget.contains(event.target)) return

    this.closeAccountMenuWithoutFocus()
  }

  syncExpandedState() {
    if (this.hasMobileToggleTarget && this.hasMobileMenuTarget) {
      const mobileMenuOpen = !this.mobileMenuTarget.classList.contains("hidden")
      this.mobileToggleTarget.setAttribute("aria-expanded", mobileMenuOpen.toString())
      this.mobileToggleTarget.setAttribute("aria-label", mobileMenuOpen ? "Close navigation menu" : "Open navigation menu")
      this.mobileMenuTarget.setAttribute("aria-hidden", (!mobileMenuOpen).toString())
    }

    if (this.hasAccountTriggerTarget && this.hasAccountMenuTarget) {
      const accountMenuOpen = !this.accountMenuTarget.classList.contains("hidden")
      const accountMenuLabel = accountMenuOpen ? "Close account menu" : "Open account menu"
      this.accountTriggerTarget.setAttribute("aria-expanded", accountMenuOpen.toString())
      this.accountTriggerTarget.setAttribute("aria-label", accountMenuLabel)
      this.accountTriggerTarget.setAttribute("title", accountMenuLabel)
      this.accountMenuTarget.setAttribute("aria-hidden", (!accountMenuOpen).toString())
    }
  }

  mobileMenuOpen() {
    return this.hasMobileMenuTarget && !this.mobileMenuTarget.classList.contains("hidden")
  }

  focusMobileMenu() {
    if (this.hasMobileMenuCloseTarget) {
      this.mobileMenuCloseTarget.focus()
      return
    }

    const firstFocusable = this.mobileMenuFocusableElements()[0]
    if (firstFocusable) firstFocusable.focus()
  }

  mobileMenuFocusableElements() {
    if (!this.hasMobileMenuPanelTarget) return []

    return Array.from(
      this.mobileMenuPanelTarget.querySelectorAll(
        "button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])"
      )
    )
  }
}
